/**
 * Maps API validation error identifiers to i18n keys.
 *
 * Auth-related identifiers (Login, UserCreation, PasswordReset, etc.) are
 * intentionally excluded — they reuse one identifier for multiple sub-cases,
 * so the API's errorMessage is used directly via fallback.
 */
const IDENTIFIER_KEY_MAP = {
  // Tools
  'Invalid ToolType':              'apiErrors.invalidToolType',
  'Invalid LocationId':            'apiErrors.invalidLocationId',
  'InvalidPriceType':              'apiErrors.invalidPriceType',
  'Already Exists':                'apiErrors.alreadyExists',
  'InvalidStatusUpdate':           'apiErrors.invalidStatusUpdate',
  'InvalidStatus':                 'apiErrors.invalidStatus',
  'InvalidPriceTypeUpdate':        'apiErrors.invalidPriceTypeUpdate',
  'UnableToUpdateMultitool':       'apiErrors.unableToUpdateMultitool',

  // Reservations
  'Invalid ProjectId':             'apiErrors.invalidProjectId',
  'Invalid ToolId':                'apiErrors.invalidToolId',
  'Invalid Tool Status':           'apiErrors.invalidToolStatus',
  'Quantity only for multi-tools': 'apiErrors.quantityOnlyForMultitools',
  'Externally Reserved':           'apiErrors.externallyReserved',
  'Already Reserved':              'apiErrors.alreadyReserved',
  'Invalid Quantity':              'apiErrors.invalidQuantity',
  'Quantity not available':        'apiErrors.quantityNotAvailable',
  'Multitool':                     'apiErrors.multitool',
  'Invalid Parent Reservation Id': 'apiErrors.invalidParentReservationId',
  'Invalid Parent Reservation Cost Type': 'apiErrors.invalidParentReservationCostType',
  'Not able to update active reservation': 'apiErrors.notAbleToUpdateActiveReservation',

  // Locations
  'Invalid LocationType':          'apiErrors.invalidLocationType',

  // Tool Groups
  'Invalid Tool Group Type':       'apiErrors.invalidToolGroupType',

  // Project Assignments (reuse existing projectAssignments.* keys)
  'Missig Quantity':               'apiErrors.missingQuantity',
  'ToolBusy':                      'projectAssignments.toolBusy',
  'QuantityTooHigh':               'projectAssignments.toolQuantityTooHigh',
  'InvalidDatesForProject':        'projectAssignments.invalidDatesForProject',

  // Projects / ERP
  'ERP Project':                   'apiErrors.erpProject',
  'No ERP assigned to Customer':   'apiErrors.noErpAssignedToCustomer',

  // Identity — expired links
  'RegistrationLinkExpired':       'registration.linkExpired',
  'ResetLinkExpired':              'common.resetLinkExpired',
};

/**
 * Resolves a localized user-facing message from an API validation error.
 *
 * Fallback chain: i18n translation → raw API errorMessage → common.somethingWentWrong
 *
 * @param {Function} t - The vue-i18n translate function (this.$t)
 * @param {Error} error - The axios error object
 * @returns {string} A localized message ready to display
 */
export function getApiErrorMessage(t, error) {
  const apiError = error?.response?.data?.[0] || error?.response?.data?.errors?.[0];

  if (apiError) {
    const { identifier, errorMessage } = apiError;
    const i18nKey = IDENTIFIER_KEY_MAP[identifier];

    if (i18nKey) {
      const translated = t(i18nKey);
      if (translated !== i18nKey) {
        return translated;
      }
    }

    if (errorMessage) {
      return errorMessage;
    }
  }

  return t('common.somethingWentWrong');
}
